import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Shield, UserPlus, Search, Mail,
  ChevronRight, Settings, Crown, CheckCircle, AlertTriangle,
  Edit, Trash2, Eye, MoreVertical, Key
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';
import {
  getTenant,
  listTenantMembers,
  inviteMember,
  updateMember,
  removeMember,
  ROLE_COLORS,
  MODULE_ICONS
} from '@/services/admin';

/**
 * TenantAdminDashboard - For Tenant Admins and Owners (non-Super Admin)
 * Allows them to manage users within their own tenant
 */
const TenantAdminDashboard = () => {
  const navigate = useNavigate();
  const { currentTenant, userRole, permissions, isTenantAdmin, hasPermission, refresh } = useTenant();
  
  const [tenant, setTenant] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'viewer' });
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    if (currentTenant?.id) {
      loadData();
    }
  }, [currentTenant?.id]);

  const loadData = async () => {
    if (!currentTenant?.id) return;
    
    try {
      setLoading(true);
      const [tenantData, membersData] = await Promise.all([
        getTenant(currentTenant.id),
        listTenantMembers(currentTenant.id)
      ]);
      
      setTenant(tenantData);
      setMembers(membersData.items || []);
    } catch (err) {
      console.error('Failed to load tenant data:', err);
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.email) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInviting(true);
    try {
      await inviteMember(currentTenant.id, inviteData);
      toast.success(`Invitation sent to ${inviteData.email}`);
      setShowInviteDialog(false);
      setInviteData({ email: '', role: 'viewer' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateMember(currentTenant.id, userId, { role: newRole });
      toast.success('Role updated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from this organization?`)) return;
    
    try {
      await removeMember(currentTenant.id, userId);
      toast.success('Member removed');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const filteredMembers = members.filter(m =>
    (m.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.full_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canInviteUsers = hasPermission('user:invite');
  const canManageRoles = hasPermission('user:assign_role');
  const canRemoveUsers = hasPermission('user:delete');

  // Available roles for assignment (based on current user's role)
  const getAssignableRoles = () => {
    if (userRole === 'admin') {
      return ['owner', 'team_member', 'auditor', 'viewer'];
    }
    if (userRole === 'owner') {
      return ['team_member', 'auditor', 'viewer'];
    }
    return ['viewer'];
  };

  if (!currentTenant) {
    return (
      <div className="p-6 text-center">
        <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h2 className="text-lg font-semibold mb-2">No Organization Selected</h2>
        <p className="text-gray-500">Please select an organization from the switcher above.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-28 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tenant-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-7 h-7 text-indigo-600" />
            {tenant?.name || 'Organization'}
          </h1>
          <p className="text-gray-500 mt-1 flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${ROLE_COLORS[userRole] || 'bg-gray-100'}`}>
              {userRole}
            </span>
            <span>•</span>
            <span className="capitalize">{tenant?.license_tier} plan</span>
          </p>
        </div>
        
        {canInviteUsers && (
          <Button 
            onClick={() => setShowInviteDialog(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="invite-member-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Team Members</p>
                <p className="text-3xl font-bold">{tenant?.member_count || members.length}</p>
                <p className="text-xs text-gray-400">of {tenant?.max_users} max</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Invites</p>
                <p className="text-3xl font-bold">{tenant?.pending_invitations || 0}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <Mail className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Enabled Modules</p>
                <div className="flex gap-1 mt-2">
                  {(tenant?.enabled_modules || []).map(m => (
                    <span key={m} className="text-xl" title={m}>
                      {MODULE_ICONS[m] || '📦'}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Key className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="members" className="space-y-4">
        <TabsList>
          <TabsTrigger value="members">Team Members</TabsTrigger>
          <TabsTrigger value="settings">Organization Info</TabsTrigger>
        </TabsList>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>
                    Manage your organization&apos;s team members
                  </CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="member-search"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredMembers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No team members found</p>
                  {canInviteUsers && (
                    <Button onClick={() => setShowInviteDialog(true)}>
                      Invite Your First Member
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredMembers.map(member => {
                    const isOwner = member.user_id === tenant?.owner_id;
                    return (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid={`member-${member.user_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-600 font-semibold">
                              {(member.full_name || member.email || '?')[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{member.full_name || member.email}</p>
                              {isOwner && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                  Tenant Owner
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {canManageRoles && !isOwner ? (
                            <Select
                              value={member.role}
                              onValueChange={(v) => handleRoleChange(member.user_id, v)}
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {getAssignableRoles().map(role => (
                                  <SelectItem key={role} value={role}>
                                    <span className="capitalize">{role.replace('_', ' ')}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`px-3 py-1 rounded text-sm capitalize ${ROLE_COLORS[member.role] || 'bg-gray-100'}`}>
                              {member.role?.replace('_', ' ')}
                            </span>
                          )}
                          
                          {canRemoveUsers && !isOwner && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveMember(member.user_id, member.email)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Organization Information</CardTitle>
              <CardDescription>
                View your organization details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-500">Organization Name</Label>
                  <p className="mt-1 font-medium">{tenant?.name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Slug</Label>
                  <p className="mt-1">
                    <code className="bg-gray-100 px-2 py-1 rounded">{tenant?.slug}</code>
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-500">License Tier</Label>
                  <p className="mt-1 capitalize font-medium">{tenant?.license_tier}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <p className="mt-1 flex items-center gap-2">
                    {tenant?.status === 'active' ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className="capitalize">{tenant?.status}</span>
                  </p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500">Allowed Email Domains</Label>
                <p className="mt-1">
                  {tenant?.domains?.length > 0 
                    ? tenant.domains.join(', ')
                    : <span className="text-gray-400">No domain restrictions</span>
                  }
                </p>
              </div>
              
              <div>
                <Label className="text-gray-500">Enabled Modules</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(tenant?.enabled_modules || []).map(module => (
                    <span 
                      key={module}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm"
                    >
                      <span>{MODULE_ICONS[module] || '📦'}</span>
                      <span className="capitalize">{module}</span>
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Created on {new Date(tenant?.created_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join {tenant?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={inviteData.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                data-testid="invite-email-input"
              />
              {tenant?.domains?.length > 0 && (
                <p className="text-xs text-gray-500">
                  Must be from: {tenant.domains.join(', ')}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Role</Label>
              <Select 
                value={inviteData.role} 
                onValueChange={(v) => setInviteData(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAssignableRoles().map(role => (
                    <SelectItem key={role} value={role}>
                      <span className="capitalize">{role.replace('_', ' ')}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInvite} 
              disabled={inviting}
              data-testid="send-invite-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {inviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantAdminDashboard;
